import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { MessageThinkingIndicator } from "./messageThinkingIndicator.js";
import { useChatScroll } from "./scroll.js";
import { ScrollEdgeFades } from "./scrollEdgeFades.js";

export interface MessageTimelineProps<T> {
  className?: string;
  messages: readonly T[];
  isLoading?: boolean;
  /**
   * Replaces the default `MessageThinkingIndicator` shown while loading (e.g.
   * a thread skeleton).
   */
  loadingIndicator?: ReactNode;
  suggestions?: ReactNode;
  /**
   * Overlays gradient fades at the top/bottom edges while content is scrolled
   * under them. Adds a wrapper element; size that wrapper via
   * `containerClassName` and keep `className` for the scroll region itself.
   */
  showsEdgeFades?: boolean;
  /** Wrapper sizing when `showsEdgeFades` is set (e.g. `flex-1`). */
  containerClassName?: string;
  /**
   * Styles the messages column — mainly to override the gap between messages,
   * for surfaces that space a message's own parts and want turns to read as
   * the wider break.
   */
  messagesClassName?: string;
  /** Accessible name for the scroll region (defaults to "Conversation"). */
  label?: string;
  children: (message: T, index: number, count: number) => ReactNode;
}

const LOADING_INDICATOR_DEBOUNCE_INTERVAL = 500; // ms

export const MessageTimeline = <T,>(props: MessageTimelineProps<T>) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);

  // Debounce the typing indicator to make it feel more natural and avoid flickering
  const isLoading = props.isLoading ?? false;
  const [isLoadingDebounced, setIsLoadingDebounced] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setIsLoadingDebounced(false);
      return;
    }

    const timeout = setTimeout(
      () => setIsLoadingDebounced(isLoading),
      LOADING_INDICATOR_DEBOUNCE_INTERVAL,
    );
    return () => clearTimeout(timeout);
  }, [isLoading]);

  // Anchor the scroll to the bottom (in absence of user-initiated scrolling)
  // Note: iOS doesn't support CSS scroll anchoring yet
  const { scrollToBottom } = useChatScroll(
    scrollContainerRef,
    scrollContentRef,
  );

  // Scroll to the last message when a new message appears or when the typing indicator changes
  const messageCount = props.messages.length;
  useEffect(() => {
    scrollToBottom();
  }, [messageCount, isLoadingDebounced, scrollToBottom]);

  const scrollRegion = (
    <div
      ref={scrollContainerRef}
      // A scrollable region has to be reachable by keyboard, or the transcript
      // can only be scrolled with a pointer. Deliberately no `role="log"`: its
      // implied live region would have a screen reader announce every chunk of
      // a streaming reply. `[&:focus-visible]` because
      // `tailwindcss-react-aria-components` repoints the `focus-visible:`
      // variant at React Aria's data attribute, which a plain element never
      // gets.
      tabIndex={0}
      aria-label={props.label ?? "Conversation"}
      className={twMerge(
        // No `outline-none`: it sets `--tw-outline-style: none`, which the
        // `outline-2` below resolves its style from, so the ring would never
        // paint.
        "flex flex-auto flex-col justify-between space-y-3 overflow-y-auto p-4 [&:focus-visible]:outline-2 [&:focus-visible]:-outline-offset-2 [&:focus-visible]:outline-primary",
        props.showsEdgeFades && "h-full",
        props.className,
      )}
    >
      <div
        ref={scrollContentRef}
        className={twMerge("flex flex-col gap-3", props.messagesClassName)}
      >
        {props.messages.map((message, index, { length }) => (
          <Fragment key={`message-${index}`}>
            {props.children(message, index, length)}
          </Fragment>
        ))}

        {isLoadingDebounced
          ? (props.loadingIndicator ?? <MessageThinkingIndicator />)
          : null}
      </div>

      {props.suggestions}
    </div>
  );

  if (!props.showsEdgeFades) {
    return scrollRegion;
  }

  return (
    <div
      className={twMerge(
        "relative min-h-0 flex-auto",
        props.containerClassName,
      )}
    >
      <ScrollEdgeFades
        scrollContainerRef={scrollContainerRef}
        scrollContentRef={scrollContentRef}
      />
      {scrollRegion}
    </div>
  );
};
